import { useEffect, useState } from 'react'

/**
 * Noticing that a new Portal OS has been published (24 Sep 2026).
 *
 * Tom, after two changes went out and his installed app kept showing the old
 * one through a full close and restart: "nothing has updated on the app
 * version". The deploy was fine both times — verified against the live bundle.
 * What was stale was the HTML shell his app had kept.
 *
 * Magic Patterns serves one small HTML page that points at a bundle whose URL
 * carries a fresh id on every publish. Get the new shell and you get the new
 * app; reuse a cached shell and you keep loading yesterday's bundle forever,
 * no matter how many times the window is closed. An installed web app has no
 * address bar and no obvious reload, so there was no way out of it either.
 *
 * So: compare the bundle this page is RUNNING against the one the server is
 * serving right now.
 *
 *   • At boot, a difference means the shell was stale. Nothing is in flight
 *     one second after load, so it just reloads — which is what "close it and
 *     open it again" was supposed to do.
 *   • While running, a difference means someone published while the app was
 *     open. It does NOT reload: that would throw away a half-written call
 *     sheet. It raises a line offering to.
 *
 * The boot reload happens at most once per session, recorded in
 * sessionStorage. If a stale shell is ever served persistently — a CDN holding
 * a copy, say — that cap is what stops the app reloading in a loop, which
 * would be far worse than being a version behind.
 *
 * ---
 *
 * **FIXED SAME DAY, and the bug is the interesting part.**
 *
 * The first version read the running bundle off `<script src>`. On the live
 * site that query finds nothing — not because the tag is missing from the
 * HTML, which is where I checked, but because it is **gone from the DOM by
 * the time any of this runs**. The served shell has it; the live page has
 * zero script elements. The published-page runtime removes them once they
 * have executed.
 *
 * So `runningBundle()` returned '', the guard below took the early return,
 * and the entire mechanism did nothing at all — silently, which is the worst
 * way for it to fail, because the symptom is identical to the bug it exists
 * to fix. Reading the served HTML and reading the live DOM are two different
 * questions, and only one of them was ever asked.
 *
 * Resource timing is the honest source: the browser records every file it
 * actually fetched, nothing can delete the entry afterwards, and it is
 * written when the file loads — before the code in it runs. The script tag is
 * kept as a fallback for anywhere it does survive (the Magic Patterns editor
 * preview, a local build).
 *
 * The comparison is on the FILE NAME rather than the whole URL, because the
 * two sources spell it differently — resource timing gives an absolute URL,
 * the shell gives whatever the attribute says — and a mismatch of spelling
 * would read as a mismatch of version and reload the app on every boot.
 */

/** The bundle URL inside a served HTML shell. */
const BUNDLE = /src="([^"]*\/bundles\/[^"]+\.js[^"]*)"/

const RELOAD_ONCE = 'portal-os:auto-reloaded'
const BOOT_DELAY_MS = 1500
const CHECK_EVERY_MS = 10 * 60 * 1000

/** The part of a bundle URL that changes per publish, spelling-independent. */
function bundleId(url: string): string {
  return (url.split('?')[0].split('#')[0].split('/').pop() ?? '').trim()
}

/**
 * What this page is actually running. '' if it cannot be told.
 *
 * Memoised: the answer cannot change without a reload, and the resource
 * buffer is finite — an app that has been open all day loading images and
 * Supabase calls could in principle fill it, and the answer read at boot is
 * the one that stays true.
 */
let runningCache = ''

function runningBundle(): string {
  if (runningCache) return runningCache

  try {
    const found = performance
      .getEntriesByType('resource')
      .map((item) => item.name)
      .find((name) => name.includes('/bundles/') && name.includes('.js'))
    if (found) {
      runningCache = found
      return runningCache
    }
  } catch {
    /* No resource timing — fall through to the tag. */
  }

  const tag = document.querySelector<HTMLScriptElement>('script[src*="/bundles/"]')
  runningCache = tag?.getAttribute('src') ?? ''
  return runningCache
}

/** What the server would serve a fresh visitor. '' on any failure — being
    offline or rate-limited must never be mistaken for a new version. */
async function publishedBundle(): Promise<string> {
  try {
    const response = await fetch(window.location.href, { cache: 'no-store' })
    if (!response.ok) return ''
    return (await response.text()).match(BUNDLE)?.[1] ?? ''
  } catch {
    return ''
  }
}

function reloadedAlready(): boolean {
  try {
    return window.sessionStorage.getItem(RELOAD_ONCE) === '1'
  } catch {
    /* Private windows throw on access. Treat that as "already", so the worst
       case is no auto-reload rather than an endless one. */
    return true
  }
}

function rememberReload() {
  try {
    window.sessionStorage.setItem(RELOAD_ONCE, '1')
  } catch {
    /* Nothing to do — reloadedAlready() has already refused in this case. */
  }
}

/**
 * True once a newer Portal OS is published and this one is still running.
 *
 * Checked shortly after load, every ten minutes, and whenever the app comes
 * back to the foreground — which on a phone is the moment that matters, since
 * that is when somebody has just opened it expecting the current version.
 */
export function useNewVersionReady(): boolean {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (typeof document === 'undefined') return
    const running = bundleId(runningBundle())
    /* Nothing identifiable to compare against: do nothing rather than guess.
       Reaching this now means neither resource timing nor a script tag named
       a bundle, which on the live site should not happen — but guessing here
       would mean reloading the app on a timer for no reason. */
    if (!running) return

    let live = true

    async function check(atBoot: boolean) {
      const latest = bundleId(await publishedBundle())
      if (!live || !latest || latest === running) return

      if (atBoot && !reloadedAlready()) {
        rememberReload()
        window.location.reload()
        return
      }
      setReady(true)
    }

    const boot = window.setTimeout(() => void check(true), BOOT_DELAY_MS)
    const every = window.setInterval(() => void check(false), CHECK_EVERY_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check(false)
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      live = false
      window.clearTimeout(boot)
      window.clearInterval(every)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return ready
}
